// src/features/upload/components/UploadPage.tsx

import { useEffect } from 'react';
import { Center, Container, Loader, Stack, Text, Title } from '@mantine/core';
import { Navigate, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useUploadStore } from '../uploadStore';
import { useUploadEngine } from '../useUploadEngine';
import { UploadDropzone } from './UploadDropzone';
import { UploadFileList } from './UploadFileList';
import { UploadQueueStatusBanner } from './UploadQueueStatusBanner';

/**
 * Full-page upload experience at /upload. The home surface for upload-only
 * users (who are route-guarded onto it), and reachable by any ingest-capable
 * user. Same engine and queue as the header drawer — the page just presents
 * them with explicit status messaging so uploaders know when they can leave.
 */
export function UploadPage() {
  const { isUploadOnly, logout } = useAuth();
  const navigate = useNavigate();
  const engine = useUploadEngine();
  const items = useUploadStore((s) => s.items);
  const closeDrawer = useUploadStore((s) => s.closeDrawer);

  // The page supersedes the drawer as the upload surface while it's open.
  useEffect(() => {
    closeDrawer();
  }, [closeDrawer]);

  const handleSignOut = () => {
    logout();
    navigate({ to: '/login' });
  };

  if (!engine.canIngest) {
    return <Navigate to="/" replace />;
  }

  if (engine.effectiveCId == null) {
    return (
      <Center h="50vh">
        <Loader size="md" />
      </Center>
    );
  }

  return (
    <Container size="md" w="100%" py="xl">
      <Stack gap="md">
        <div>
          <Title order={2}>Upload Videos</Title>
          <Text c="dimmed" size="sm" mt={4}>
            Files are processed automatically after upload.
          </Text>
        </div>

        <UploadQueueStatusBanner items={items} showSignOut={isUploadOnly} onSignOut={handleSignOut} />

        <UploadDropzone
          addFilesWithMeta={engine.addFilesWithMeta}
          ingestcfgs={engine.ingestcfgs}
          selectedIngestcfgId={engine.selectedIngestcfgId}
          onSelectIngestcfg={engine.setSelectedIngestcfgId}
          hasMultiple={engine.hasMultiple}
          isRestricted={engine.isRestricted}
          cId={engine.effectiveCId}
          hasFiles={items.length > 0}
        />

        <UploadFileList
          onCancel={engine.cancelUpload}
          onRetry={engine.retryUpload}
          onRetryAllFailed={engine.retryAllFailed}
          onClearCompleted={engine.clearCompleted}
        />
      </Stack>
    </Container>
  );
}
